<template>
	<template v-if="readonly">
		<event-color-field :value="valueProxy" ref="field" :readonly="true" :tabindex="tabindex" v-bind="$attrs"></event-color-field>
	</template>
	<template v-else>
		<anchor-element :tag="EventColorField" :chain="chain" :value="valueProxy" :tabindex="tabindex" @focus="fieldFocus" @blur="fieldBlur" @click="inputClick" @keydown="keyDown" ref="field" v-bind="$attrs"></anchor-element>
		<boat-element tag="div" :chain="chain" class="fd-drop fd-form event-color-drop" @mousedown.stop.prevent="">
			<event-color-wheel v-model="valueProxy" :required="required" @clickset="clickSetColor"></event-color-wheel>
		</boat-element>
	</template>
	<input v-if="property" type="hidden" :name="property" v-model.number="propertyValue">
</template>

<script setup lang="ts">
import {ref, computed} from "vue";
import {createChain} from "@/ui/paneltree/PanelTree.ts";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import EventColorField from "./EventColorField.vue";
import EventColorWheel from "@fd/editors/color/EventColorWheel.vue";

const props = withDefaults(defineProps<{
	modelValue: number;
	tabindex?: number;
	/* If set the component will synchronize model value with hidden input element with this name.
	 * modelValue is used only to initialize input/hidden element */
	property?: string;
	/* If required unselected state (0 / dot in the middle) isn't allowed */
	required?:boolean;
	readonly?:boolean;
}>(), { required: false, readonly: false, tabindex:0 });
const emit = defineEmits(['update:modelValue']);
const propertyValue = ref<number>(props.modelValue);
const chain = createChain({
	rule: {
		ah: 0, av: 100,
		bh: 0, bv: 0,
		x: 0, y: 0
	}
}, "event-color");
const valueProxy = computed<number>({
	get():number { return props.property ? propertyValue.value : props.modelValue; },
	set(v:number):void {
		if (props.property) propertyValue.value = v;
		else emit('update:modelValue', v);
	}
});
const field = ref<InstanceType<typeof EventColorField> | null>(null);

let blurTimeout = 0;

function clickSetColor(e:MouseEvent) {
	chain.hide();
}

function inputClick(e:MouseEvent) {
	if (!chain.visible.value) {
		chain.show();
		e.preventDefault();
		e.stopPropagation();
	}
}

function fieldFocus() {
	chain.visible.value = true;
	if (blurTimeout) {
		clearTimeout(blurTimeout);
		blurTimeout = 0;
	}
}
function fieldBlur() {
	blurTimeout = setTimeout(() => {
		chain.visible.value = false;
	}, 150);
}

// Movements
const LEFT:number[] = [7, 0, 3, 4, 5, 6, 7, 7, 7, 8, 9, 10, 11];
const RIGHT:number[] = [1, 1, 1, 2, 3, 4, 5, 0, 9, 10, 11, 12, 1];
const UP:number[] = [4, 2, 3, 4, 4, 4, 5, 6, 7, 8, 0, 12, 1];
const DOWN:number[] = [10, 12, 1, 2, 0, 6, 7, 8, 9, 10, 10, 10, 11];

function moveToIndex(current:number, moveArray:number[]):number {
	let next = moveArray[current];
	if (props.required && next === 0) next = moveArray[0];
	return next;
}

function keyDown(e: KeyboardEvent) {
	const key = e.keyCode;
	let current = valueProxy.value;
	if (current >= 0 && current < LEFT.length) {
		if (37 === key /* LEFT */) {
			valueProxy.value = moveToIndex(current, LEFT);
			e.preventDefault();
		} else if (38 === key /* UP */) {
			valueProxy.value = moveToIndex(current, UP);
			e.preventDefault();
		} else if (39 === key /* RIGHT */) {
			valueProxy.value = moveToIndex(current, RIGHT);
			e.preventDefault();
		} else if (40 === key /* DOWN */) {
			valueProxy.value = moveToIndex(current, DOWN);
			e.preventDefault();
		} else if (8 === key /* BACKSPACE */) {
			valueProxy.value = 0;
		} else if (27 === key /* ESC */) {
			field.value.$el.blur();
			e.preventDefault();
		}
	}
}

</script>

<style>
.event-color-drop {
	padding: 8px;
}
</style>